import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Person,
  WhatsApp,
  Instagram,
  Facebook,
  Schedule,
  Refresh
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventService } from '../../services/eventService';

const EventParticipationManagement = ({ eventId, onClose }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedParticipation, setSelectedParticipation] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const queryClient = useQueryClient();

  // Buscar participações do evento
  const { data: eventData, isLoading } = useQuery({
    queryKey: ['event-participations', eventId],
    queryFn: () => eventService.getEventWithParticipations(eventId),
  });

  const event = eventData?.data?.event;
  const participations = event?.participations || [];

  // Mutations
  const updateParticipationStatusMutation = useMutation({
    mutationFn: ({ participationId, status }) => 
      eventService.updateParticipationStatus(participationId, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['event-participations', eventId]);
      setSnackbar({ open: true, message: 'Status atualizado com sucesso!', severity: 'success' });
      setStatusDialog(false);
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar status', severity: 'error' });
    }
  });

  const handleOpenStatusDialog = (participation) => {
    setSelectedParticipation(participation);
    setNewStatus(participation.status);
    setStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialog(false);
    setSelectedParticipation(null);
    setNewStatus('');
  };

  const handleUpdateStatus = () => {
    if (selectedParticipation && newStatus) {
      updateParticipationStatusMutation.mutate({
        participationId: selectedParticipation.id,
        status: newStatus
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'confirmed': return 'info';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Aprovado';
      case 'rejected': return 'Rejeitado';
      case 'confirmed': return 'Confirmado';
      case 'pending': return 'Pendente';
      default: return status;
    }
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const normalizePhoneForWa = (input) => {
    if (!input) return null;
    const digits = String(input).replace(/\D/g, '');
    if (digits.length === 10 || digits.length === 11) return `55${digits}`;
    return digits;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Participações - {event?.title}
            </Typography>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: '#FF6B35',
                color: '#FF6B35',
                '&:hover': { borderColor: '#FF8A65' }
              }}
            >
              Fechar
            </Button>
          </Box>

          {participations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Person sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Nenhuma participação registrada
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>DJ</TableCell>
                    <TableCell>Contato</TableCell>
                    <TableCell>Horário</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participations.map((participation) => (
                    <TableRow key={participation.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            src={participation.profile_image}
                            sx={{ width: 40, height: 40 }}
                          >
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ color: '#FF6B35' }}>
                              {participation.dj_name}
                            </Typography>
                            {participation.confirmation_code && (
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Código: {participation.confirmation_code}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {participation.whatsapp && (
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <WhatsApp sx={{ fontSize: 16, color: '#25D366' }} />
                              <Link
                                href={`https://wa.me/${normalizePhoneForWa(participation.whatsapp)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ color: '#25D366', fontWeight: 600 }}
                              >
                                {participation.whatsapp}
                              </Link>
                            </Box>
                          )}
                          {participation.instagram && (
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Instagram sx={{ fontSize: 16, color: '#E4405F' }} />
                              <Typography variant="body2">
                                {participation.instagram}
                              </Typography>
                            </Box>
                          )}
                          {participation.facebook && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Facebook sx={{ fontSize: 16, color: '#1877F2' }} />
                              <Typography variant="body2">
                                {participation.facebook}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Schedule sx={{ fontSize: 16, color: '#00D4FF' }} />
                          <Typography variant="body2">
                            {formatTime(participation.time_slot)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(participation.status)}
                          color={getStatusColor(participation.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Alterar Status">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenStatusDialog(participation)}
                              sx={{ color: '#00D4FF' }}
                            >
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog para alterar status */}
      <Dialog open={statusDialog} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Alterar Status da Participação
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, color: '#FF6B35' }}>
              DJ: {selectedParticipation?.dj_name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
              Horário: {selectedParticipation && formatTime(selectedParticipation.time_slot)}
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Novo Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="Novo Status"
              >
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="approved">Aprovado</MenuItem>
                <MenuItem value="rejected">Rejeitado</MenuItem>
                <MenuItem value="confirmed">Confirmado</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancelar</Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={updateParticipationStatusMutation.isPending || !newStatus}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {updateParticipationStatusMutation.isPending ? 
              <CircularProgress size={20} /> : 'Atualizar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventParticipationManagement;